import { useState, useEffect, useCallback } from 'react'
import { cmsService } from '../../services/cmsService'
import RichTextEditor from '../../components/cms/RichTextEditor'

// ─── Default content mirrors the live About.jsx page ───────────────────────
const DEFAULT_CONTENT = {
    hero: {
        title: 'About TenderCentriX',
        description:
            'TenderCentriX is a specialist tender discovery platform built exclusively for health and social care providers across the UK and the Republic of Ireland. We remove complexity, save time, and give providers clear, reliable visibility into the opportunities that matter.',
    },
    mission: {
        title: 'Our Mission',
        text: 'To give every care provider, large or small, clear, dependable and complete access to the tenders they need, without noise, confusion or wasted time. We exist to make opportunity discovery simple, accurate and accessible.',
    },
    vision: {
        title: 'Our Vision',
        text: 'A sector where no provider misses an opportunity because of fragmented systems, inconsistent portals or information buried across dozens of websites. We aim to become the most trusted source of tender intelligence for health and social care in the UK and Ireland.',
    },
    values: {
        sectionTitle: 'Our Values',
        sectionSubtitle: 'The core principles that guide how we build our platform and serve our community.',
        items: [
            { title: 'Clarity', text: 'We believe providers deserve transparent, accurate and timely information, not clutter or confusion.' },
            { title: 'Reliability', text: 'We build systems that care providers can depend on, day after day, with no hidden gaps or guesswork.' },
            { title: 'Fair Access', text: 'We level the playing field. Smaller providers should have the same visibility and opportunity as larger groups.' },
            { title: 'Simplicity', text: 'Complex systems waste time. TenderCentriX is designed to be clean, practical and easy to use.' },
            { title: 'Integrity', text: 'Accuracy matters. We present information precisely as it is, without bias, distortion or artificial weighting.' },
            { title: 'Continuous Improvement', text: "We listen, we refine, and we evolve ensuring TenderCentriX moves forward with the sector's needs." },
        ],
    },
    promise: {
        sectionTitle: 'Our Promise to Providers',
        paragraphs: [
            'TenderCentriX is built to give care providers clear, dependable access to the opportunities that matter.',
            'Core tender discovery and visibility will remain central to the platform and will continue to be improved without fragmenting access or creating unnecessary complexity. Where the platform expands to include additional systems such as evidence management, compliance tools or care management capabilities, these will be offered transparently as optional enhancements, not barriers to core use.',
            'Pricing for the tender discovery platform is designed to remain fair and accessible. Any price changes beyond our control, such as inflationary or operating cost increases, will always be communicated clearly and responsibly.',
        ],
        highlightedText: 'Our focus is long-term: supporting provider growth, compliance and quality of care through practical, trusted systems.',
    },
    cta: {
        title: 'Ready to get started?',
        primaryButtonLabel: 'Start 14-Day Free Trial',
        primaryButtonLink: '/signup',
        secondaryButtonLabel: 'Contact Us',
        secondaryButtonLink: '/contact',
        trustBadgeText: 'Card required. No charge if cancelled during trial.',
    },
}

// ─── Shared form primitives ─────────────────────────────────────────────────

function Field({ label, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-admin-muted uppercase tracking-wider">{label}</label>
            {children}
        </div>
    )
}

function Input({ value, onChange, placeholder }) {
    return (
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all"
        />
    )
}

function Textarea({ value, onChange, rows = 3, placeholder }) {
    return (
        <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className="w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all resize-y"
        />
    )
}

function SectionCard({ title, description, onSave, saving, saved, error, children }) {
    return (
        <div className="bg-white border border-admin-border rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-admin-border bg-slate-50 flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-sm font-bold text-admin-heading">{title}</h3>
                    {description && <p className="text-xs text-admin-muted mt-0.5">{description}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    {error && <span className="text-xs text-status-error font-medium">{error}</span>}
                    {saved && !error && (
                        <span className="text-xs text-status-success font-bold flex items-center gap-1">
                            <CheckIcon className="w-3.5 h-3.5" /> Saved
                        </span>
                    )}
                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="px-4 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                        {saving ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" /> : null}
                        {saving ? 'Saving…' : 'Save Section'}
                    </button>
                </div>
            </div>
            <div className="p-6 space-y-5">{children}</div>
        </div>
    )
}

// ─── Section editors ────────────────────────────────────────────────────────

function HeroSection({ data, onChange, onSave, saving, saved, error }) {
    return (
        <SectionCard
            title="Hero Banner"
            description="The top banner text shown at the very top of the About page."
            onSave={onSave}
            saving={saving}
            saved={saved}
            error={error}
        >
            <Field label="Page Title">
                <Input value={data.title} onChange={(v) => onChange({ ...data, title: v })} />
            </Field>
            <Field label="Sub-description">
                <RichTextEditor value={data.description} onChange={(v) => onChange({ ...data, description: v })} />
            </Field>
        </SectionCard>
    )
}

function MissionVisionSection({ mission, vision, onMissionChange, onVisionChange, onSave, saving, saved, error }) {
    return (
        <SectionCard
            title="Mission & Vision"
            description="The two-column Mission and Vision block."
            onSave={onSave}
            saving={saving}
            saved={saved}
            error={error}
        >
            <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <p className="text-xs font-bold text-brand-navy uppercase tracking-widest border-b border-admin-border pb-2">Mission</p>
                    <Field label="Title">
                        <Input value={mission.title} onChange={(v) => onMissionChange({ ...mission, title: v })} />
                    </Field>
                    <Field label="Body Text">
                        <RichTextEditor value={mission.text} onChange={(v) => onMissionChange({ ...mission, text: v })} />
                    </Field>
                </div>
                <div className="space-y-4">
                    <p className="text-xs font-bold text-brand-navy uppercase tracking-widest border-b border-admin-border pb-2">Vision</p>
                    <Field label="Title">
                        <Input value={vision.title} onChange={(v) => onVisionChange({ ...vision, title: v })} />
                    </Field>
                    <Field label="Body Text">
                        <RichTextEditor value={vision.text} onChange={(v) => onVisionChange({ ...vision, text: v })} />
                    </Field>
                </div>
            </div>
        </SectionCard>
    )
}

function ValuesSection({ data, onChange, onSave, saving, saved, error }) {
    const updateItem = (index, field, value) => {
        const items = data.items.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        )
        onChange({ ...data, items })
    }

    return (
        <SectionCard
            title="Our Values"
            description="Section heading and the six value cards."
            onSave={onSave}
            saving={saving}
            saved={saved}
            error={error}
        >
            <div className="grid md:grid-cols-2 gap-5 pb-2">
                <Field label="Section Title">
                    <Input value={data.sectionTitle} onChange={(v) => onChange({ ...data, sectionTitle: v })} />
                </Field>
                <Field label="Section Subtitle">
                    <Input value={data.sectionSubtitle} onChange={(v) => onChange({ ...data, sectionSubtitle: v })} />
                </Field>
            </div>

            <div className="border-t border-admin-border pt-5">
                <p className="text-xs font-bold text-admin-muted uppercase tracking-wider mb-4">Value Cards</p>
                <div className="grid md:grid-cols-2 gap-4">
                    {data.items.map((item, i) => (
                        <div key={i} className="p-4 bg-slate-50 border border-admin-border rounded-lg space-y-3">
                            <p className="text-[10px] font-bold text-admin-muted uppercase tracking-widest">Card {i + 1}</p>
                            <Field label="Title">
                                <Input value={item.title} onChange={(v) => updateItem(i, 'title', v)} />
                            </Field>
                            <Field label="Description">
                                <RichTextEditor value={item.text} onChange={(v) => updateItem(i, 'text', v)} />
                            </Field>
                        </div>
                    ))}
                </div>
            </div>
        </SectionCard>
    )
}

function PromiseSection({ data, onChange, onSave, saving, saved, error }) {
    const updateParagraph = (index, value) => {
        const paragraphs = data.paragraphs.map((p, i) => (i === index ? value : p))
        onChange({ ...data, paragraphs })
    }

    return (
        <SectionCard
            title="Our Promise to Providers"
            description="The full-width promise text block with highlighted closing statement."
            onSave={onSave}
            saving={saving}
            saved={saved}
            error={error}
        >
            <Field label="Section Title">
                <Input value={data.sectionTitle} onChange={(v) => onChange({ ...data, sectionTitle: v })} />
            </Field>

            <div className="space-y-3">
                <label className="text-xs font-bold text-admin-muted uppercase tracking-wider">Body Paragraphs</label>
                {data.paragraphs.map((para, i) => (
                    <div key={i} className="flex gap-3 items-start">
                        <span className="mt-2.5 text-[10px] font-bold text-admin-muted bg-slate-100 border border-admin-border rounded px-1.5 py-0.5 flex-shrink-0">P{i + 1}</span>
                        <div className="flex-1"><RichTextEditor value={para} onChange={(v) => updateParagraph(i, v)} /></div>
                    </div>
                ))}
            </div>

            <Field label="Highlighted Closing Statement">
                <RichTextEditor
                    value={data.highlightedText}
                    onChange={(v) => onChange({ ...data, highlightedText: v })}
                />
            </Field>
        </SectionCard>
    )
}

function CtaSection({ data, onChange, onSave, saving, saved, error }) {
    return (
        <SectionCard
            title="Call to Action Banner"
            description="The bottom CTA strip with headline, buttons, and trust badge."
            onSave={onSave}
            saving={saving}
            saved={saved}
            error={error}
        >
            <Field label="Headline">
                <Input value={data.title} onChange={(v) => onChange({ ...data, title: v })} />
            </Field>

            <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-3 p-4 bg-slate-50 border border-admin-border rounded-lg">
                    <p className="text-[10px] font-bold text-admin-muted uppercase tracking-widest">Primary Button</p>
                    <Field label="Label">
                        <Input value={data.primaryButtonLabel} onChange={(v) => onChange({ ...data, primaryButtonLabel: v })} />
                    </Field>
                    <Field label="Link / Path">
                        <Input value={data.primaryButtonLink} onChange={(v) => onChange({ ...data, primaryButtonLink: v })} />
                    </Field>
                </div>
                <div className="space-y-3 p-4 bg-slate-50 border border-admin-border rounded-lg">
                    <p className="text-[10px] font-bold text-admin-muted uppercase tracking-widest">Secondary Button</p>
                    <Field label="Label">
                        <Input value={data.secondaryButtonLabel} onChange={(v) => onChange({ ...data, secondaryButtonLabel: v })} />
                    </Field>
                    <Field label="Link / Path">
                        <Input value={data.secondaryButtonLink} onChange={(v) => onChange({ ...data, secondaryButtonLink: v })} />
                    </Field>
                </div>
            </div>

            <Field label="Trust Badge Text">
                <Input value={data.trustBadgeText} onChange={(v) => onChange({ ...data, trustBadgeText: v })} />
            </Field>
        </SectionCard>
    )
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function AboutCmsPage() {
    const [content, setContent] = useState(DEFAULT_CONTENT)
    const [pageLoading, setPageLoading] = useState(true)
    const [pageError, setPageError] = useState(null)

    const [sectionState, setSectionState] = useState({
        hero: { saving: false, saved: false, error: null },
        missionVision: { saving: false, saved: false, error: null },
        values: { saving: false, saved: false, error: null },
        promise: { saving: false, saved: false, error: null },
        cta: { saving: false, saved: false, error: null },
    })

    useEffect(() => {
        cmsService.getAbout()
            .then((res) => {
                if (res?.data) setContent({ ...DEFAULT_CONTENT, ...res.data })
            })
            .catch(() => {
                // No existing CMS data yet — defaults are shown, that's fine
            })
            .finally(() => setPageLoading(false))
    }, [])

    const setSaving = (section, state) =>
        setSectionState((prev) => ({ ...prev, [section]: { ...prev[section], ...state } }))

    const saveSection = useCallback(async (sectionKey, payload) => {
        setSaving(sectionKey, { saving: true, saved: false, error: null })
        try {
            await cmsService.updateAboutSection(sectionKey, payload)
            setSaving(sectionKey, { saving: false, saved: true, error: null })
            setTimeout(() => setSaving(sectionKey, { saved: false }), 3000)
        } catch (err) {
            setSaving(sectionKey, { saving: false, saved: false, error: err.message || 'Save failed' })
        }
    }, [])

    if (pageLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Page header */}
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-lg font-bold text-admin-heading">About Us — Content Editor</h2>
                    <p className="text-sm text-admin-muted mt-1">
                        Edit each section independently. Changes are saved to the CMS and reflected on the live About page.
                    </p>
                </div>
                <a
                    href={`${import.meta.env.VITE_FRONTEND_URL || 'https://tendercentrix.com'}/about`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-admin-muted border border-admin-border rounded hover:bg-slate-100 transition-all"
                >
                    <ExternalLinkIcon className="w-3.5 h-3.5" />
                    View Live Page
                </a>
            </div>

            {pageError && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{pageError}</div>
            )}

            {/* Section editors */}
            <HeroSection
                data={content.hero}
                onChange={(v) => setContent((c) => ({ ...c, hero: v }))}
                onSave={() => saveSection('hero', content.hero)}
                {...sectionState.hero}
            />

            <MissionVisionSection
                mission={content.mission}
                vision={content.vision}
                onMissionChange={(v) => setContent((c) => ({ ...c, mission: v }))}
                onVisionChange={(v) => setContent((c) => ({ ...c, vision: v }))}
                onSave={() => saveSection('missionVision', { mission: content.mission, vision: content.vision })}
                {...sectionState.missionVision}
            />

            <ValuesSection
                data={content.values}
                onChange={(v) => setContent((c) => ({ ...c, values: v }))}
                onSave={() => saveSection('values', content.values)}
                {...sectionState.values}
            />

            <PromiseSection
                data={content.promise}
                onChange={(v) => setContent((c) => ({ ...c, promise: v }))}
                onSave={() => saveSection('promise', content.promise)}
                {...sectionState.promise}
            />

            <CtaSection
                data={content.cta}
                onChange={(v) => setContent((c) => ({ ...c, cta: v }))}
                onSave={() => saveSection('cta', content.cta)}
                {...sectionState.cta}
            />
        </div>
    )
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function CheckIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
    )
}

function SpinnerIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
    )
}

function ExternalLinkIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
    )
}
